import { useState, type ReactElement } from 'react';
import { Marca } from '@selflevel/ui';
import { AREA_SUGGESTIONS, type AreaSuggestion } from '../store/catalogo';
import { upsertArea, upsertHabit, upsertIdentity, updateSettings } from '../store/mutations';
import { zonaDelDispositivo } from '../store/session';

/**
 * EL ALTA. Dos pasos, después de la cuenta (spec «cuenta obligatoria»).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Antes no había registro y todo vivía solo en el dispositivo; quien cambiaba de
 * móvil lo perdía. Ahora la puerta pide la cuenta (usuario y contraseña, sin correo)
 * y el alta empieza después, en las áreas. Quien entra con una cuenta que ya tiene
 * cosas no pasa por aquí: lo suyo baja y la app abre con ello.
 *
 * Y la identidad —«soy alguien que…»— se propone al final, ya escrita, para
 * aceptarla o cambiarla. Pedirle a alguien que declare quién es en la pantalla
 * uno es pedirle demasiado; enseñarle una frase que encaja es otra cosa.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function Alta(): ReactElement {
  const [paso, setPaso] = useState(1);
  const [elegidas, setElegidas] = useState<string[]>([]);
  const [habitos, setHabitos] = useState<string[]>([]);
  const [guardando, setGuardando] = useState(false);

  const areasElegidas = AREA_SUGGESTIONS.filter((a) => elegidas.includes(a.key));
  const sugerencias = areasElegidas.flatMap((area) =>
    area.habits.map((h) => ({ ...h, areaKey: area.key, key: `${area.key}:${h.name}` })),
  );

  const terminar = async () => {
    setGuardando(true);
    // La misma fuente que «hoy» y que cada casilla: la zona del dispositivo.
    const tz = zonaDelDispositivo();
    const areaIds = new Map<string, string>();

    for (const area of areasElegidas) {
      const id = await upsertArea({
        name: area.name,
        marca: area.marca,
        colorToken: area.colorToken,
      });
      areaIds.set(area.key, id);
      await upsertIdentity({ areaId: id, text: area.identity });
    }

    for (const sugerencia of sugerencias) {
      if (!habitos.includes(sugerencia.key)) continue;
      const areaId = areaIds.get(sugerencia.areaKey);
      if (!areaId) continue;
      await upsertHabit({
        areaId,
        name: sugerencia.name,
        marca: sugerencia.marca,
        cadence: sugerencia.cadence,
        target: sugerencia.target ?? null,
        unit: sugerencia.unit ?? null,
      });
    }

    // El nombre ya se pidió en la puerta: aquí no se toca.
    await updateSettings({
      tz,
      onboardedAt: new Date().toISOString(),
    });
  };

  return (
    <main className="screen" style={{ paddingBottom: 'var(--sp-8)' }}>
      {paso === 1 ? (
        <section className="stack stack--loose" style={{ marginTop: 'var(--sp-6)' }}>
          <div>
            <p className="t-caption t-dim">Paso 1 de 2</p>
            <h1 className="t-title-1">¿Qué partes de tu vida quieres mirar?</h1>
            <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-2)' }}>
              Dos o tres. Se pueden cambiar luego.
            </p>
          </div>

          <ul className="stack">
            {AREA_SUGGESTIONS.map((area) => (
              <TarjetaArea
                key={area.key}
                area={area}
                seleccionada={elegidas.includes(area.key)}
                onToggle={() =>
                  setElegidas((prev) =>
                    prev.includes(area.key)
                      ? prev.filter((k) => k !== area.key)
                      : [...prev, area.key],
                  )
                }
              />
            ))}
          </ul>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={elegidas.length === 0}
            onClick={() => setPaso(2)}
          >
            Seguir
          </button>
        </section>
      ) : null}

      {paso === 2 ? (
        <section className="stack stack--loose" style={{ marginTop: 'var(--sp-6)' }}>
          <div>
            <p className="t-caption t-dim">Paso 2 de 2</p>
            <h1 className="t-title-1">Elige por dónde empezar</h1>
            <p className="t-sm t-dim" style={{ marginTop: 'var(--sp-2)' }}>
              Uno o dos bastan. De verdad: empezar con ocho es la forma clásica de dejarlo en marzo.
            </p>
          </div>

          <ul className="stack">
            {sugerencias.map((sugerencia) => {
              const on = habitos.includes(sugerencia.key);
              return (
                <li key={sugerencia.key}>
                  <button
                    type="button"
                    className={`habit ${on ? 'habit--done' : ''}`}
                    style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                    aria-pressed={on}
                    onClick={() =>
                      setHabitos((prev) =>
                        prev.includes(sugerencia.key)
                          ? prev.filter((k) => k !== sugerencia.key)
                          : [...prev, sugerencia.key],
                      )
                    }
                  >
                    <span
                      className={`habit__disc${on ? '' : ' habit__disc--hueco'}`}
                      style={{
                        ['--area' as string]: `var(${
                          AREA_SUGGESTIONS.find((a) => a.key === sugerencia.areaKey)?.colorToken ??
                          '--dim-1'
                        })`,
                      }}
                    >
                      <Marca id={sugerencia.marca} size={22} polarity={on ? 'area' : 'neutra'} />
                    </span>
                    <span className="grow">
                      <span className="habit__name">{sugerencia.name}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={guardando}
            onClick={() => void terminar()}
          >
            {habitos.length === 0 ? 'Empezar sin hábitos' : `Empezar con ${habitos.length}`}
          </button>
          <button type="button" className="btn btn--bare" onClick={() => setPaso(1)}>
            Atrás
          </button>
        </section>
      ) : null}
    </main>
  );
}

function TarjetaArea({
  area,
  seleccionada,
  onToggle,
}: {
  area: AreaSuggestion;
  seleccionada: boolean;
  onToggle: () => void;
}): ReactElement {
  return (
    <li>
      <button
        type="button"
        className="habit"
        style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
        aria-pressed={seleccionada}
        onClick={onToggle}
      >
        <span
          className={`habit__disc${seleccionada ? '' : ' habit__disc--hueco'}`}
          style={{ ['--area' as string]: `var(${area.colorToken})` }}
        >
          <Marca id={area.marca} size={22} polarity={seleccionada ? 'area' : 'neutra'} />
        </span>
        <span className="grow">
          <span className="habit__name">{area.name}</span>
          <span className="habit__meta" style={{ display: 'block' }}>
            {area.identity}
          </span>
        </span>
      </button>
    </li>
  );
}
